import { NO_TEXT, type Translated } from '@myre/common-i18n';
import { type Element } from '@myre/ui-primitive-components';

import { InjectionToken, TemplateRef } from '@angular/core';

export const TREE_TABLE_CONFIG = new InjectionToken('TreeTableConfig');
export const TREE_TABLE_USER_CONFIG = new InjectionToken('TreeTableUserConfig');

export interface IBulkFoldable {
  foldAll(on: boolean, self?: boolean): void;
}

export interface ITreeTableColumn<T> {
  id?: number | string;
  idString?: string;
  label: Translated;
  width: number;
  template?: TemplateRef<any>;

  getValue(element: T): any;
  onClick?(element: T): (() => void) | null;
  getElements?(element: T): Element[];
}

export interface ITreeTableColumnConfig<T> extends ITreeTableColumn<T> {
  sticky?: boolean;
  stickyEnd?: boolean;
}

export interface ITreeTableConfig<T = any> {
  columns: ITreeTableColumn<T>[];
  columnIds?: string[];
  sticky?: boolean;

  isSeparator?(index: number, element: Data<T>): boolean;

  hasSubItems?(index: number, element: Data<T>): boolean;
}

export interface IData<T> {
  isSeparator?: boolean;
  /** The heading a separator row shows. */
  separatorLabel?: Translated;
  subItems?: IData<T>[];
  isOpen?: boolean;
  content?: T;
  id?: string;
}

export class Data<T> implements IData<T> {
  public isSeparator?: boolean;
  public separatorLabel?: Translated;
  public subItemsClass?: Data<T>[];
  public parentItem?: Data<T>;
  public isOpen?: boolean;
  public hasSubItems?: boolean;
  public isFullyOpen?: boolean;
  public lastFoldableLevel?: boolean;
  public content?: T;
  public id?: string;

  constructor(data: IData<T>, parentItem?: Data<T>) {
    this.isSeparator = data.isSeparator;
    this.separatorLabel = data.separatorLabel;
    this.isOpen = data.isOpen;
    this.parentItem = parentItem;
    this.content = data.content;

    this.id = `${data.id}_${TreeTableConfig.hasSubItems(data)}`;

    if (data.subItems) {
      this.subItemsClass = data.subItems.map(d => new Data(d, this));
      this.isFullyOpen = false;
      this.lastFoldableLevel = data.subItems.every(
        d => !TreeTableConfig.hasSubItems(d),
      );
    } else {
      this.isFullyOpen = true;
    }
  }

  public open?(open: boolean): void {
    this.isOpen = open;

    this._handleChildOpen();
  }

  private _handleChildOpen?(): void {
    this.isFullyOpen =
      this.isOpen &&
      TreeTableConfig.hasSubItems(this) &&
      this.subItemsClass.every(sub => sub.isFullyOpen);

    if (this.parentItem) {
      this.parentItem._handleChildOpen();
    }
  }
}

export class TreeTableConfig<T> implements ITreeTableConfig<T> {
  public columns: ITreeTableColumnConfig<T>[];
  public columnIds: string[];
  public sticky: boolean;

  constructor(columns: ITreeTableColumn<T>[] = [], sticky: boolean = false) {
    this.columns = columns.map((c, index) => ({
      ...c,
      id: c.id || index,
    }));

    this.sticky = sticky;

    if (this.columns.length) {
      const firstColumn = this.columns[0];

      if (this.sticky) {
        firstColumn.sticky = true;
      }

      this.columns.push({
        id: this.columns.length,
        width: 24,
        label: NO_TEXT,
        getValue: () => '',
        stickyEnd: this.sticky,
      });
    }

    for (const column of this.columns) {
      column.idString = `${column.id}`;
    }

    this.columnIds = this.columns.map(c => c.idString);
  }

  public static isSeparator<A>(element: Data<A>): boolean {
    return element.isSeparator;
  }

  public static keepRow<A>(
    element: Data<A>,
    filterRow: (data: Data<A>) => boolean,
    onlyOpened: boolean = false,
  ): boolean {
    if (
      filterRow(element) ||
      TreeTableConfig.hasShownSubItems(element, filterRow, onlyOpened)
    ) {
      return true;
    }

    let parent = element.parentItem;

    while (parent) {
      if (filterRow(parent)) {
        return true;
      }

      parent = parent.parentItem;
    }

    return false;
  }

  public static hasSubItems<A>(element: Data<A> | IData<A>): boolean {
    const array =
      element instanceof Data ? element.subItemsClass : element.subItems;

    return Array.isArray(array) && !!array.length;
  }

  public static hasShownSubItems<A>(
    element: Data<A>,
    filterRow: (data: Data<A>) => boolean,
    onlyOpened: boolean = true,
  ): boolean {
    if (onlyOpened && !element.isOpen) {
      return false;
    }

    return (
      TreeTableConfig.hasSubItems(element) &&
      element.subItemsClass.some(subRow =>
        TreeTableConfig.keepRow(subRow, filterRow, onlyOpened),
      )
    );
  }
}
